import React from 'react'
import { PullToRefreshContainer } from '@mobi/component-library/Common/PullToRefreshContainer'
import { useScrollRestoreOverride } from '@core/Areas/AppHeader/Components/ScrollRestoration/hooks'
import { triggerHapticFeedback } from '@core/Utils/hapticFeedback/hapticFeedback'
import { useSharedBetsData } from '@core/Areas/BetLoop/hooks/useSharedBetsData'
import { EmptyMessage } from '@core/Areas/BetLoop/Components/Common/EmptyMessage'
import type { SortOrder, SortFilter } from '@core/Areas/BetLoop/types'
import type { BetLoopFeedParams } from '@core/Areas/BetLoop/helpers/api/types'
import { QuickShareButton } from '@core/Areas/BetLoop/Components/QuickShareButton'
import { SortFeed } from '@core/Areas/BetLoop/Components/Common/SortFeed'
import { SharedBetCard } from '@core/Areas/BetLoop/Components/Common/SharedBetCard'
import { FeedContainerStyled } from '@core/Areas/BetLoop/Components/Common/BetLoop.styles'
import { SkeletonLoader } from '@core/Areas/BetLoop/Components/Common/SkeletonLoader'
import { useAccountFollowListData } from '@core/Areas/BetLoop/hooks/useAccountFollowListData'
import { useRefetchFeedDataOnBetslipClosed } from '@core/Areas/BetLoop/hooks/useRefetchFeedDataOnBetslipClosed'

let sessionSortOrder: SortOrder = 'sortAsc'
let sessionSortFilter: SortFilter = 'JumpTime'

export const FollowingFeed: React.FC<{ feedDisplayType?: BetLoopFeedParams['type'] }> = ({
  feedDisplayType = 'racing',
}) => {
  useRefetchFeedDataOnBetslipClosed('following')

  const [sortOrder, setSortOrder] = React.useState<SortOrder>(sessionSortOrder)
  const [sortFilter, setSortFilter] = React.useState<SortFilter>(sessionSortFilter)

  React.useEffect(() => {
    sessionSortOrder = sortOrder
    sessionSortFilter = sortFilter
  }, [sortFilter, sortOrder])

  const { isEmptyState: hasNoFollowingProfiles, isLoading: isLoadingFollowingAccounts } =
    useAccountFollowListData('following')

  const { isLoading, data, refetch, LoadMoreButton } = useSharedBetsData({
    sortFilter,
    sortOrder,
    feedType: 'following',
    type: feedDisplayType,
  })

  const shouldRenderLoader = !data || isLoading || isLoadingFollowingAccounts
  useScrollRestoreOverride({ hasPageLoaded: !shouldRenderLoader && !hasNoFollowingProfiles })

  if (hasNoFollowingProfiles) {
    return (
      <>
        <EmptyMessage
          messageType='feed'
          title='Welcome to your Following Feed!'
          description={`Your feed gives easy access to\n Tips from Profiles you follow.`}
        />

        <QuickShareButton />
      </>
    )
  }

  if (!isLoading && data?.pages && (data.pages.length === 0 || data.pages[0].length === 0)) {
    return (
      <>
        <EmptyMessage
          messageType='feed'
          title='No tips from the profiles you follow'
          description={`Check back later or explore other\n tipsters to see the latest picks.`}
        />

        <QuickShareButton />
      </>
    )
  }

  return (
    <>
      <SortFeed
        sortFilter={sortFilter}
        sortOrder={sortOrder}
        handleSortFilterChange={setSortFilter}
        handleSortOrderChange={setSortOrder}
      />

      <PullToRefreshContainer
        isLoading={shouldRenderLoader}
        onRefresh={refetch}
        onReachingRefreshThreshold={triggerHapticFeedback}
        skeletonLoader={
          <FeedContainerStyled>
            <SkeletonLoader />
          </FeedContainerStyled>
        }
      >
        <FeedContainerStyled>
          {data?.pages.map(feedPage =>
            feedPage.map(sharedBet => (
              <SharedBetCard key={sharedBet.postId} sharedBet={sharedBet} />
            ))
          )}

          <LoadMoreButton />
        </FeedContainerStyled>
      </PullToRefreshContainer>

      <QuickShareButton />
    </>
  )
}
